import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";

export default function BranchToFactory() {
  const { user, branchId } = useAuth();

  const [demands, setDemands] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    from_branch_id: "",
    to_branch_id: "",
    item_id: "",
    quantity: "",
    unit: "Kg",
  });

  // ================= FETCH =================
  useEffect(() => {
    fetchDemands();
    fetchBranches();
    fetchInventory();
  }, []);

  const fetchDemands = async () => {
    const res = await fetch("/api/demands", {
      credentials: "include",
    });
    const data = await res.json();

    if (data.status === "success") {
      setDemands(data.data);
    }
  };

  const fetchBranches = async () => {
    const res = await fetch("/api/branches", {
      credentials: "include",
    });
    const data = await res.json();

    if (data.status === "success") {
      setBranches(data.data);
    }
  };

  const fetchInventory = async () => {
    const res = await fetch("/api/inventory/items", {
      credentials: "include",
    });
    const data = await res.json();

    if (data.status === "success") {
      setInventory(data.data);
    }
  };

  // ================= CREATE DEMAND =================
  const submitDemand = async () => {
    const payload = {
      ...form,
      from_branch_id:
        user?.role === "super_admin" ? form.from_branch_id : branchId,
    };

    const res = await fetch("/api/demands", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message);
      return;
    }

    setShowModal(false);
    fetchDemands();
  };

  // ================= APPROVE =================
  const approveDemand = async (id) => {
    const res = await fetch(`/api/demands/${id}/approve`, {
      method: "POST",
      credentials: "include",
    });

    const data = await res.json();

    if (data.status !== "success") {
      alert(data.message);
      return;
    }

    fetchDemands();
  };

  // ================= FORMAT DATE =================
  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-10">
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-serif">Branch → Factory Demands</h1>

          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
          >
            + Raise Demand
          </button>
        </div>

        {/* DEMANDS LIST */}
        <div className="space-y-4">
          {demands.map((d) => (
            <div
              key={d.id}
              className="flex justify-between items-center bg-white/5 border border-white/10 rounded-xl p-4"
            >
              <div>
                <p className="font-medium">
                  {d.from_branch} → {d.to_branch}
                </p>

                <p className="text-sm text-gray-400">
                  {d.item} | {d.qty} {d.unit}
                </p>

                <p className="text-xs text-gray-500">{formatDate(d.date)}</p>
              </div>

              <div className="flex items-center gap-3">
                <StatusBadge status={d.status} />

                {d.status === "pending" && (
                  <button
                    onClick={() => approveDemand(d.id)}
                    className="px-4 py-2 rounded-lg bg-green-500 text-white"
                  >
                    Approve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ================= MODAL ================= */}
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <h2 className="text-xl font-serif mb-6">Raise Demand</h2>

            <div className="space-y-4">
              {/* TO BRANCH */}
              {/* FROM BRANCH (ONLY FOR SUPER ADMIN) */}
              {user?.role === "super_admin" && (
                <select
                  value={form.from_branch_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      from_branch_id: Number(e.target.value),
                    })
                  }
                  className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                >
                  <option value="">Select From Branch</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}

              {/* FROM BRANCH (READ ONLY FOR STAFF) */}
              {user?.role !== "super_admin" && (
                <div className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10 text-gray-300">
                  From:{" "}
                  {branches.find((b) => b.id === Number(branchId))?.name ||
                    "Loading..."}
                </div>
              )}

              {/* TO BRANCH */}
              <select
                value={form.to_branch_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    to_branch_id: Number(e.target.value),
                  })
                }
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option value="">Select To Branch</option>

                {branches
                  .filter((b) =>
                    user?.role === "super_admin"
                      ? b.id !== form.from_branch_id
                      : b.id !== Number(branchId),
                  )
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
              </select>

              {/* ITEM */}
              <select
                value={form.item_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    item_id: Number(e.target.value),
                  })
                }
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option value="">Select Item</option>
                {inventory.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>

              {/* QTY */}
              <input
                type="number"
                placeholder="Quantity"
                value={form.quantity}
                onChange={(e) =>
                  setForm({
                    ...form,
                    quantity: e.target.value,
                  })
                }
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              {/* UNIT */}
              <select
                value={form.unit}
                onChange={(e) =>
                  setForm({
                    ...form,
                    unit: e.target.value,
                  })
                }
                className="w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option>Kg</option>
                <option>Litres</option>
                <option>Pieces</option>
              </select>
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </button>

              <button
                onClick={submitDemand}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                Submit
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

// ================= STATUS BADGE =================
function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    running: "bg-emerald-500/20 text-emerald-400",
    completed: "bg-gray-500/20 text-gray-400",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs ${
        map[status] || "bg-white/10"
      }`}
    >
      {status}
    </span>
  );
}

// ================= MODAL =================
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}
